/**
 * Single source of truth for the dynamic form questions of a white-label portal.
 *
 * Every form that can be configured per website is listed in FORM_QUESTIONS.
 * Only the card ("New Video Request") form is wired up today; to make another
 * page dynamic, add an entry here and read it with getFormQuestions(..., "<formKey>").
 *
 * Question fields:
 * - `key`             must match the field the form uses (usually the API payload key)
 * - `label`           what the admin sees in the "Form Questions" modal
 * - `locked`          always shown and always required; the admin cannot change it
 * - `requirable`      the admin may mark it as required (only for inputs with a value)
 * - `defaultVisible`  / `defaultRequired`  used when the portal has never been configured
 *
 * The per-portal setting is stored in website_type.form_questions as one JSON envelope
 * with an entry per form, so new forms (customer, user, ...) just add a key under `forms`:
 *   {
 *     "version": 1,
 *     "forms": {
 *       "card":     { "<questionKey>": { "visible": bool, "required": bool } },
 *       "customer": { ... }
 *     }
 *   }
 * The legacy flat shape { "<formKey>": {...} } is still read; saves always use the envelope.
 *
 * Adding a form later:
 *   1. add `{ key: "<formKey>", label, questions: [...] }` to FORM_QUESTIONS below
 *   2. in that form's component: `const q = getFormQuestions(subDomainData.form_questions, "<formKey>")`
 *      and wrap each question in `isQuestionVisible(q, "<questionKey>")`
 *   The admin modal picks the new form up automatically (a tab appears per form).
 */
export const FORM_QUESTIONS_VERSION = 1;

export const FORM_QUESTIONS = [
    {
        key: "card",
        label: "New Video Request (Card)",
        questions: [
            { key: "card_title",           label: "What title should we use for this video?",               locked: true },
            { key: "dropbox_link",         label: "Gdrive/Dropbox folder download link for raw video",      locked: true },
            { key: "youtube_aspect_ratio", label: "Which format do you want your video in?",                requirable: true, defaultRequired: true },
            { key: "video_purpose",        label: "What type of content is this order for?",                requirable: true, defaultRequired: true },
            { key: "voice_note",           label: "Voice notes" },
            { key: "additional_info",      label: "Anything else we should know about this video?",        requirable: true },
            { key: "special_instructions", label: "Special instructions for this video",                    requirable: true },
            { key: "youtube_helper",       label: "Need youtube helper?" },
            { key: "has_end_card",         label: "Does this video require an End Card?" },
            { key: "subtitles",            label: "Add subtitles?" },
            { key: "repurpose",            label: "Do you want us to repurpose this video?" },
        ],
    },
];

/**
 * Returns the `forms` map ({ [formKey]: { [questionKey]: {...} } }) from whatever the API
 * returned: the versioned envelope, the legacy flat map, a JSON string, or null.
 */
const parseStored = (raw) => {
    let stored = raw;
    if (typeof stored === "string") {
        try {
            stored = JSON.parse(stored);
        } catch (e) {
            stored = null;
        }
    }
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return null;
    if ("forms" in stored) {
        return stored.forms && typeof stored.forms === "object" ? stored.forms : null;
    }
    return stored;
};

/** Wrap a forms map in the versioned envelope that the API stores. */
export const buildFormQuestionsPayload = (forms) => ({
    version: FORM_QUESTIONS_VERSION,
    forms: forms && typeof forms === "object" ? forms : {},
});

const findForm = (formKey) => FORM_QUESTIONS.find(({ key }) => key === formKey);

/** Settings a question has when the portal never saved one. */
function defaultQuestionSettings(question) {
    if (question.locked) {
        return { visible: true, required: true };
    }
    const visible = question.defaultVisible !== false;
    return { visible, required: visible && Boolean(question.requirable && question.defaultRequired) };
}

/**
 * Accepts whatever came back from the API (object, JSON string, or null) and returns
 * a full settings map for one form: { [questionKey]: { visible, required } }.
 * Questions missing from the stored JSON fall back to their defaults.
 *
 * @param {object|string|null} raw  subDomainData.form_questions (or a customer row's form_questions)
 * @param {string} formKey          key from FORM_QUESTIONS, e.g. "card"
 */
export function getFormQuestions(raw, formKey) {
    const form = findForm(formKey);
    if (!form) return {};
    const storedForm = parseStored(raw)?.[formKey];
    const settings = {};
    form.questions.forEach((question) => {
        const defaults = defaultQuestionSettings(question);
        const entry = storedForm?.[question.key];
        if (question.locked || !entry || typeof entry !== "object") {
            settings[question.key] = defaults;
            return;
        }
        const visible = entry.visible === undefined ? defaults.visible : Boolean(entry.visible);
        settings[question.key] = {
            visible,
            required: visible && Boolean(question.requirable) && Boolean(entry.required),
        };
    });
    return settings;
}

/**
 * Settings map for every form in FORM_QUESTIONS: { [formKey]: { [questionKey]: { visible, required } } }.
 * This is the `forms` part of the stored envelope; wrap it with buildFormQuestionsPayload() before saving.
 */
export function normalizeFormQuestions(raw) {
    const all = {};
    FORM_QUESTIONS.forEach(({ key }) => {
        all[key] = getFormQuestions(raw, key);
    });
    return all;
}

/** Unknown question keys are always shown, so new fields never disappear by accident. */
export const isQuestionVisible = (settings, questionKey) => settings?.[questionKey]?.visible !== false;

export const isQuestionRequired = (settings, questionKey) => Boolean(settings?.[questionKey]?.required);

/** True when a form value counts as answered (rich-text HTML with no text counts as empty). */
export function hasAnswer(value) {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.some(hasAnswer);
    const text = String(value).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    return text !== "";
}
